import React, { useMemo, useState } from 'react';
import { formatDecimal as fmt } from '../helpers/paymentsHelper';

const SampleTable = ({
  data,
  week,
  handleEditClick,
  handleChange,
  handleCancelEdit,
  handleUpdateAll,
  handleSaveClick,
  editMode,
  editedValues,
  itemLoad
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const getByPath = (obj, path) =>
    path.split('.').reduce((acc, key) => (acc?.[key] ?? undefined), obj);

  const cmp = (a, b) => {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;

    const aNum = Number(a);
    const bNum = Number(b);
    const bothNumbers = !Number.isNaN(aNum) && !Number.isNaN(bNum);
    if (bothNumbers) return aNum - bNum;

    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
  };

  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    return [...data].sort((a, b) => dir * cmp(getByPath(a, sortConfig.key), getByPath(b, sortConfig.key)));
  }, [data, sortConfig]);

  const filteredData = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return sortedData;
    return sortedData.filter((item) =>
      (item.account_name ?? '').toString().toLowerCase().includes(q)
    );
  }, [sortedData, searchTerm]);

  const formatMinutes = (totalMins) => {
  const mins = Number(totalMins) || 0;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h ? `${h}h ${m} ${m === 1 ? 'minute' : 'minutes'}` : `${m} ${m === 1 ? 'minute' : 'minutes'}`;
};

  return (
    <div className="relative w-full">
      <div className="relative flex w-1/4 items-center">
        <input
          type="text"
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ paddingTop: '0.44rem', paddingBottom: '0.44rem' }}
          className="w-full pl-4 pr-10 text-gray-800 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 placeholder-gray-400 placeholder-opacity-100 font-light text-sm"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <svg
            className="w-5 h-5 text-gray-700"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            ></path>
          </svg>
        </div>
      </div>

      <table className="table-auto border-collapse min-w-full">
        <thead className="sticky top-0 z-20 bg-white">
          <tr className='bg-white border-b'>
            <th
              className="sticky top-0 left-0 z-20 p-2 bg-white cursor-pointer text-left"
              onClick={() => handleSort('account_name')}>
              Drivers{' '}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1">
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === 'account_name' && sortConfig.direction === 'asc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />

                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === 'account_name' && sortConfig.direction === 'desc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
              </svg>
            </th>
            <th
              className="sticky top-0 z-10 bg-white p-2 cursor-pointer text-left"
              onClick={() => handleSort('snapshot.trips')}>
              Trips{' '}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1">
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === 'snapshot.trips' && sortConfig.direction === 'asc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />

                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === 'snapshot.trips' && sortConfig.direction === 'desc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
              </svg>
            </th>
            <th
              className="sticky top-0 z-10 bg-white p-2 cursor-pointer text-left"
              onClick={() => handleSort('completed_trips')}>
              Trips Completed
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1">
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === 'completed_trips' && sortConfig.direction === 'asc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />

                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === 'completed_trips' && sortConfig.direction === 'desc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
              </svg>
            </th>
            <th
              className="sticky top-0 z-10 bg-white p-2 cursor-pointer text-left"
              onClick={() => handleSort('kms')}>
              KMs{' '}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1">
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === 'kms' && sortConfig.direction === 'asc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />

                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === 'kms' && sortConfig.direction === 'desc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
              </svg>
            </th>
            <th
              className="sticky top-0 z-10 bg-white p-2 cursor-pointer text-left"
              onClick={() => handleSort('duration')}>
              Duration{' '}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1">
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === 'duration' && sortConfig.direction === 'asc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === 'duration' && sortConfig.direction === 'desc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
              </svg>
            </th>
            <th
              className="sticky top-0 z-10 bg-white p-2 cursor-pointer text-left"
              onClick={() => handleSort('cancelled')}>
              Cancelled{' '}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1">
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === 'cancelled' && sortConfig.direction === 'asc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === 'cancelled' && sortConfig.direction === 'desc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
              </svg>
            </th>
            <th
              className="sticky top-0 z-10 bg-white p-2 cursor-pointer text-left"
              onClick={() => handleSort('snapshot.bonus')}>
              Bonus{' '}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1">
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === 'snapshot.bonus' && sortConfig.direction === 'asc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === 'snapshot.bonus' && sortConfig.direction === 'desc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
              </svg>
            </th>
            <th
              className="sticky top-0 z-10 bg-white p-2 cursor-pointer text-left"
              onClick={() => handleSort('snapshot.performance_expense')}>
              Performance{' '}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1">
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === 'snapshot.performance_expense' && sortConfig.direction === 'asc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === 'snapshot.performance_expense' && sortConfig.direction === 'desc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
              </svg>
            </th>
            <th
              className="sticky top-0 z-10 bg-white p-2 cursor-pointer text-left"
              onClick={() => handleSort('snapshot.fuel')}>
              Fuel{' '}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1">
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === 'snapshot.fuel' && sortConfig.direction === 'asc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === 'snapshot.fuel' && sortConfig.direction === 'desc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
              </svg>
            </th>
            <th
              className="sticky top-0 z-10 bg-white p-2 cursor-pointer text-left"
              onClick={() => handleSort('snapshot.km_overuse')}>
              KMs+{' '}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1">
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === 'snapshot.km_overuse' && sortConfig.direction === 'asc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === 'snapshot.km_overuse' && sortConfig.direction === 'desc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
              </svg>
            </th>
            <th
              className="sticky top-0 z-10 bg-white p-2 cursor-pointer text-left"
              onClick={() => handleSort('snapshot.deduction')}>
              Deduction{' '}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1">
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === 'snapshot.deduction' && sortConfig.direction === 'asc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === 'snapshot.deduction' && sortConfig.direction === 'desc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
              </svg>
            </th>
            <th
              className="p-2 cursor-pointer text-left"
              onClick={() => handleSort('snapshot.driver_balance_security')}>
              Driver Balance Security{' '}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1">
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === 'snapshot.driver_balance_security' && sortConfig.direction === 'asc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === 'snapshot.driver_balance_security' && sortConfig.direction === 'desc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
              </svg>
            </th>
            <th
              className="sticky top-0 z-10 bg-white p-2 cursor-pointer text-left"
              onClick={() => handleSort('snapshot.access_to_site')}>
              Access to Site{' '}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1">
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === 'snapshot.access_to_site' && sortConfig.direction === 'asc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === 'snapshot.access_to_site' && sortConfig.direction === 'desc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
              </svg>
            </th>
            <th
              className="sticky top-0 z-10 bg-white p-2 cursor-pointer text-left"
              onClick={() => handleSort('snapshot.other')}>
              Other{' '}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1">
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === 'snapshot.other' && sortConfig.direction === 'asc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === 'snapshot.other' && sortConfig.direction === 'desc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
              </svg>
            </th>
            <th
              className="sticky top-0 z-10 bg-white p-2 cursor-pointer text-left"
              onClick={() => handleSort('snapshot.uber')}>
              Uber{' '}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1">
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === 'snapshot.uber' && sortConfig.direction === 'asc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === 'snapshot.uber' && sortConfig.direction === 'desc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
              </svg>
            </th>
            <th
              className="sticky top-0 z-10 bg-white p-2 cursor-pointer text-left"
              onClick={() => handleSort('snapshot.bolt')}>
              Bolt{' '}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1">
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === 'snapshot.bolt' && sortConfig.direction === 'asc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === 'snapshot.bolt' && sortConfig.direction === 'desc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
              </svg>
            </th>
            <th
              className="sticky top-0 z-10 bg-white p-2 cursor-pointer text-left"
              onClick={() => handleSort('snapshot.payout')}>
              Payout{' '}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1">
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === 'snapshot.payout' && sortConfig.direction === 'asc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === 'snapshot.payout' && sortConfig.direction === 'desc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
              </svg>
            </th>
            <th className="sticky top-0 z-10 bg-white p-2 cursor-pointer text-left">
            {Object.values(editMode).filter((v) => v).length > 1 && (
              <div className="flex justify-end mb-2">
                <button
                  type="button"
                  onClick={handleUpdateAll}
                  className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-500"
                  disabled={itemLoad}
                >
                  {itemLoad ? 'Loading...' : 'Update All'}
                </button>
              </div>
            )}
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredData?.map((item) => (
            <React.Fragment key={item.account_id}>
              <tr
                className="border-t border-b p-2 bg-white"
               >
                <td className="sticky left-0 z-10 bg-white whitespace-nowrap w-64 min-w-[16rem] px-2 py-2col-span-2">
                  {item.account_name}
                </td>
                <td className="col-span-1 border-b p-2 text-left">
                  {item.snapshot.trips}
                </td>
                <td className="col-span-1 border-b p-2 text-left">
                  {item.completed_trips}
                </td>
                <td className="col-span-1 border-b p-2 text-left">
                  {fmt(item.kms)}
                </td>
                <td className="col-span-1 border-b p-2 text-left">
                  {formatMinutes(item.duration)}
                </td>
                <td className="col-span-1 border-b p-2 text-left">
                  {item.cancelled}
                </td>

                {["bonus", "performance_expense", "fuel", "km_overuse", "deduction", "driver_balance_security", "access_to_site", "other", "uber", "bolt"].map((field) => {
                  const creditFields = ["bonus", "other", "uber", "bolt"];
                  const textColorClass = creditFields.includes(field) ? "text-green-600" : "text-red-600";
                  const editing = editMode[item.snapshot.snapshot_id];
                  const editedRow = editedValues[item.snapshot.snapshot_id];
                  const editedValue = editedRow?.[field];

                  return (
                    <td key={field} className={`p-2 border-b ${textColorClass}`}>
                      {editing ? (
                        <input
                          type="number"
                          className="border p-1 w-16"
                          value={editedValue ?? 0}
                          onChange={(e) => handleChange(item.snapshot.snapshot_id, field, e.target.value)}
                        />
                      ) : (
                        fmt(item.snapshot[field] || 0)
                      )}
                    </td>
                  );
                })}
                <td className="col-span-1 border-b p-2 text-left">
                  {fmt(item.snapshot.payout)}
                </td>
                
                
                <td className="col-span-3 border-b p-2 space-x-1">


                  {editMode[item.snapshot.snapshot_id] ? (
                    <>
                    <button 
                      onClick={() => handleSaveClick(item.snapshot.snapshot_id)}
                      className={itemLoad ? "bg-gray-700 p-2 rounded hover:bg-gray-200" : "bg-green-700 p-2 rounded hover:bg-gray-200" }>
                       {itemLoad ? (
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                          >
                            <circle
                              cx="10"
                              cy="10"
                              r="8"
                              stroke="white"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeDasharray="50"
                              strokeDashoffset="10"
                            />
                          </svg>                  
                          ) : (
                            <svg
                              width="19"
                              height="17"
                              viewBox="0 0 19 17"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg">
                              <path
                                d="M3 0C2.44772 0 2 0.447715 2 1V15C2 15.5523 2.44772 16 3 16H15C15.5523 16 16 15.5523 16 15V4.41421C16 4.149 15.8946 3.89464 15.7071 3.70711L12.2929 0.292893C12.1054 0.105357 11.851 0 11.5858 0H3ZM9 1.5H14.5V4H12C11.4477 4 11 3.55228 11 3V1.5ZM4 8C4 7.44772 4.44772 7 5 7H13C13.5523 7 14 7.44772 14 8V14H4V8ZM6 9V10H12V9H6Z"
                                fill="white"
                              />
                            </svg>
                          )
                        }
                    </button>

                    <button
                      onClick={() => handleCancelEdit(item.snapshot.snapshot_id)}
                      className="p-2 bg-red-700 rounded hover:bg-red-200"
                    >
                      <svg width="24" height="17" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <line x1="18" y1="6" x2="6" y2="18" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                        <line x1="6" y1="6" x2="18" y2="18" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                  </button>
                  </>) : (
                      <>
                  <button
                  type="button"
                  onClick={() => handleEditClick(item.snapshot.snapshot_id, item)}
                  className="p-2 bg-purple-600 rounded hover:bg-purple-200">
                    <svg
                      width="19"
                      height="17"
                      viewBox="0 0 19 17"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M12.5389 2.59738L15.3481 5.41668C15.4665 5.53546 15.4665 5.72924 15.3481 5.84802L8.54613 12.6744L5.65589 12.9963C5.2697 13.04 4.94268 12.7119 4.98628 12.3243L5.30707 9.42372L12.1091 2.59738C12.2274 2.47861 12.4205 2.47861 12.5389 2.59738ZM17.5843 1.88162L16.0645 0.35632C15.5911 -0.118773 14.8218 -0.118773 14.3453 0.35632L13.2428 1.46279C13.1244 1.58156 13.1244 1.77535 13.2428 1.89412L16.052 4.71342C16.1704 4.83219 16.3635 4.83219 16.4818 4.71342L17.5843 3.60695C18.0577 3.12874 18.0577 2.35671 17.5843 1.88162ZM11.9596 10.8177V13.9996H1.99327V3.99766H9.15034C9.25 3.99766 9.34343 3.95702 9.41507 3.88826L10.6609 2.63802C10.8976 2.40047 10.7294 1.99727 10.3961 1.99727H1.49495C0.669613 1.99727 0 2.66927 0 3.49756V14.4997C0 15.328 0.669613 16 1.49495 16H12.4579C13.2832 16 13.9529 15.328 13.9529 14.4997V9.56749C13.9529 9.23305 13.5511 9.0674 13.3144 9.30182L12.0686 10.5521C12.0001 10.624 11.9596 10.7177 11.9596 10.8177Z"
                        fill="white"
                      />
                    </svg>
                  </button>

                  <a
                  href={`/payments/drivers/${item.user_id}?start_date_filter=${encodeURIComponent(week?.from || '')}&end_date_filter=${encodeURIComponent(week?.to || '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-gray-700 rounded hover:bg-gray-200 inline-flex">
                  <svg
                      width="20"
                      height="17"
                      viewBox="0 0 20 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M8 7C8 7.53043 8.21071 8.03914 8.58579 8.41421C8.96086 8.78929 9.46957 9 10 9C10.5304 9 11.0391 8.78929 11.4142 8.41421C11.7893 8.03914 12 7.53043 12 7C12 6.46957 11.7893 5.96086 11.4142 5.58579C11.0391 5.21071 10.5304 5 10 5C9.46957 5 8.96086 5.21071 8.58579 5.58579C8.21071 5.96086 8 6.46957 8 7Z"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M19 7C16.6 11 13.6 13 10 13C6.4 13 3.4 11 1 7C3.4 3 6.4 1 10 1C13.6 1 16.6 3 19 7Z"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </a>
                  </>
                    )}
                </td>
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>
    {/* pagination */}
    {/* eof pagination */}
    </div>
  );
};

export default SampleTable;